import { state, type ScreenName } from "./state";

export const SCREEN_TITLES: Record<ScreenName, string> = {
  overview: "Home",
  studio: "New Outfit",
  hair: "New Hairstyle",
  closet: "My Closet",
  pricing: "Pricing",
  account: "Account",
  faq: "FAQ",
};

type ScreenChangeListener = (screen: ScreenName) => void;
const screenChangeListeners: ScreenChangeListener[] = [];

export function onScreenChange(listener: ScreenChangeListener): void {
  screenChangeListeners.push(listener);
}

function isScreenName(value: string): value is ScreenName {
  return Object.prototype.hasOwnProperty.call(SCREEN_TITLES, value);
}

export function parseScreenFromHash(): ScreenName {
  const hashValue = window.location.hash.replace("#", "").trim();
  if (hashValue === "billing") return "pricing";
  return isScreenName(hashValue) ? hashValue : "overview";
}

function setDrawerOpen(isOpen: boolean): void {
  document.body.classList.toggle("drawer-open", isOpen);
  const toggle = document.getElementById("nav-toggle");
  const drawer = document.getElementById("nav-drawer");
  if (toggle) toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
  if (drawer) drawer.setAttribute("aria-hidden", isOpen ? "false" : "true");
}

interface SetActiveScreenOptions {
  updateHash?: boolean;
}

export function setActiveScreen(
  screenName: string,
  options: SetActiveScreenOptions = {},
): void {
  const { updateHash = true } = options;
  const nextScreen: ScreenName = isScreenName(screenName)
    ? screenName
    : screenName === "billing"
    ? "pricing"
    : "overview";
  state.activeScreen = nextScreen;

  const navLinks = document.querySelectorAll<HTMLElement>(".nav-link");
  navLinks.forEach(link => {
    link.classList.toggle("is-active", link.dataset.screen === nextScreen);
  });

  const screens = document.querySelectorAll<HTMLElement>(".screen");
  screens.forEach(screen => {
    screen.classList.toggle("is-active", screen.dataset.screen === nextScreen);
  });

  screenChangeListeners.forEach(listener => {
    try {
      listener(nextScreen);
    } catch (error: unknown) {
      console.error("Screen-change listener failed:", error);
    }
  });

  if (updateHash) {
    const nextHash = nextScreen === "overview" ? "" : `#${nextScreen}`;
    if (window.location.hash !== nextHash) {
      const url = `${window.location.pathname}${window.location.search}${nextHash}`;
      history.pushState(null, "", url);
    }
  }

  setDrawerOpen(false);
}

export function bindNavigation(): void {
  const navToggle = document.getElementById("nav-toggle");
  const navBackdrop = document.getElementById("nav-backdrop");
  const brandButton = document.getElementById("brand-button");
  const navLinks = document.querySelectorAll<HTMLElement>(".nav-link");
  const quickNavButtons = document.querySelectorAll<HTMLElement>(".quick-nav");
  const ctaButton = document.getElementById("cta-get-started");

  navToggle?.addEventListener("click", () => {
    const isOpen = document.body.classList.contains("drawer-open");
    setDrawerOpen(!isOpen);
  });
  navBackdrop?.addEventListener("click", () => setDrawerOpen(false));
  document.addEventListener("keydown", event => {
    if (event.key === "Escape") setDrawerOpen(false);
  });

  brandButton?.addEventListener("click", event => {
    event.preventDefault();
    setActiveScreen("overview");
  });

  navLinks.forEach(link => {
    link.addEventListener("click", () => {
      setActiveScreen(link.dataset.screen || "overview");
    });
  });

  quickNavButtons.forEach(button => {
    button.addEventListener("click", () => {
      setActiveScreen(button.dataset.target || "overview");
    });
  });

  ctaButton?.addEventListener("click", () => {
    const target = state.user ? "studio" : "account";
    setActiveScreen(target);
  });

  window.addEventListener("hashchange", () => {
    setActiveScreen(parseScreenFromHash(), { updateHash: false });
  });
}
